import Link from '@atlaskit/link';
import { Flex, Stack, xcss } from '@atlaskit/primitives';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { SettingsList } from '../../components/settings-list';
import { ROWS_PER_PAGE } from '../../components/types';
import { useGetCodePlannerSettingsController } from '../hooks/use-get-code-planner-settings-controller';
import { useSetCodePlannerSettingsController } from '../hooks/use-set-code-planner-settings-controller';
import type { CodePlannerSetting, JiraProject } from '../types';

import { JiraProjectToggle } from './jira-project-toggle';
import messages from './messages';

const styles = xcss({ height: '50px' });

type JiraProjectsTableProps = {
	projects: JiraProject[];
	projectsLoaded: boolean;
	settingsCache: Map<string, boolean>;
	onSettingsCacheChange: (settings: Map<string, boolean>) => void;
};

export const JiraProjectsTable = ({
	projects,
	projectsLoaded,
	settingsCache,
	onSettingsCacheChange,
}: JiraProjectsTableProps) => {
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [tableDataLoaded, setTableDataLoaded] = useState<boolean>(false);
	const { formatMessage } = useIntl();

	const {
		getCodePlannerSettings,
		loading: codePlannerSettingsLoading,
		error: codePlannerSettingsError,
	} = useGetCodePlannerSettingsController();
	const { setCodePlannerSettings } = useSetCodePlannerSettingsController();

	const tableHead = useMemo(
		() => ({
			cells: [
				{
					key: 'project-key-head-cell',
					content: formatMessage(messages.projectKey),
					isSortable: true,
				},
				{
					key: 'project-name-head-cell',
					content: formatMessage(messages.projectName),
					isSortable: true,
				},
				{
					key: 'actions-head-cell',
					content: formatMessage(messages.enabledStatus),
					isSortable: false,
				},
			],
		}),
		[formatMessage],
	);

	const filteredProjects = useMemo(() => {
		return projects.filter((project) =>
			project.name.toLowerCase().includes(searchTerm.toLowerCase()),
		);
	}, [projects, searchTerm]);

	const currentProjects = useMemo(() => {
		const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
		const endIndex = startIndex + ROWS_PER_PAGE;
		return filteredProjects.slice(startIndex, endIndex);
	}, [filteredProjects, currentPage]);

	useEffect(() => {
		if (currentProjects.length > 0) {
			const jiraProjectIds = currentProjects.map((project) => project.id);
			const cachedIds = Array.from(settingsCache.keys());

			const nonCachedProjects = jiraProjectIds.filter((id) => !cachedIds.includes(id));

			if (nonCachedProjects.length > 0) {
				getCodePlannerSettings(nonCachedProjects).then(
					(settings: Map<string, CodePlannerSetting>) => {
						const updatedSettings = new Map(settingsCache);

						settings.forEach((setting, id) => {
							updatedSettings.set(id, setting.value.value.code_planner_enabled);
						});

						nonCachedProjects.forEach((id) => {
							if (!settings.has(id)) {
								updatedSettings.set(id, false);
							}
						});

						onSettingsCacheChange(updatedSettings);
					},
				);
			}

			setTableDataLoaded(true);
		}
	}, [currentProjects, getCodePlannerSettings, onSettingsCacheChange, settingsCache]);

	const handleProjectSearch = useCallback((input: string) => {
		setCurrentPage(1);
		setSearchTerm(input);
	}, []);

	const handleToggleChange = async (jiraProjectAri: string, value: boolean) => {
		setCodePlannerSettings([
			{
				containerId: jiraProjectAri,
				value,
			},
		]);

		const updatedSettings = new Map(settingsCache);
		updatedSettings.set(jiraProjectAri, value);

		onSettingsCacheChange(updatedSettings);
	};

	const projectRows = filteredProjects.map((project) => {
		const { id, key, name, webUrl } = project;

		return {
			key: `row-${key}-${name}`,
			cells: [
				{
					key: `key-${key}`,
					content: (
						<Flex xcss={styles} alignItems="center">
							<Link href={webUrl} appearance="subtle" target="_blank" rel="noopener noreferrer">
								{key}
							</Link>
						</Flex>
					),
				},
				{
					key: `name-${name}`,
					content: (
						<Link href={webUrl} appearance="subtle" target="_blank" rel="noopener noreferrer">
							{name}
						</Link>
					),
				},
				{
					key: `actions-${key}`,
					content: (
						<JiraProjectToggle
							jiraProjectAri={id}
							onChange={handleToggleChange}
							value={settingsCache.get(id) ?? false}
							settingsError={codePlannerSettingsError}
						/>
					),
				},
			],
		};
	});

	return (
		<Stack testId="jira-projects-table">
			<SettingsList
				headingText={formatMessage(messages.projectTitle)}
				tableHead={tableHead}
				tableRows={projectRows}
				currentPage={currentPage}
				setCurrentPage={setCurrentPage}
				loading={!projectsLoaded || !tableDataLoaded || codePlannerSettingsLoading}
				error={codePlannerSettingsError}
				onSearch={handleProjectSearch}
				searchPlaceholder={formatMessage(messages.searchPlaceholder)}
			/>
		</Stack>
	);
};
